<template>
  <div
    ref="carousel"
    data-testid="CarouselCard"
    :class="{
      'fluid-container': alignment === 'left',
      container: alignment === 'top',
      'mx-auto grid lg:grid-cols-4': true,
    }"
  >
    <div
      :class="{
        'lg:col-span-1 lg:my-auto': alignment === 'left',
        'lg:col-span-4 lg:flex lg:justify-between': alignment === 'top',
      }"
    >
      <div
        :class="{
          'text-center lg:text-start': true,
        }"
      >
        <h2
          :class="{
            'mb-4 text-28px font-bold leading-[3rem] md:text-35px': true,
          }"
        >
          {{ title }}
        </h2>
        <p
          :class="{
            'px-5 text-sm leading-8 md:px-20 md:text-base': true,
            'lg:max-w-xl lg:px-0 lg:text-start': true,
          }"
        >
          {{ subtitle }}
        </p>
      </div>

      <div
        v-if="button?.text"
        :class="{
          'mb-10 mt-5 self-end justify-self-center md:mb-6': true,
          'lg:mb-4 lg:justify-self-start': alignment === 'left',
        }"
      >
        <Button :button-props="button" />
      </div>

      <CarouselCardControl
        :class="{
          'mt-3 hidden gap-4 lg:flex lg:items-center lg:justify-start': true,
          'lg:hidden': alignment === 'top' || (cards?.length || 0) <= 3,
        }"
      />
    </div>

    <div
      :class="{
        grid: true,
        'lg:col-span-3': alignment === 'left',
        'lg:col-span-4 lg:mt-6': alignment === 'top',
      }"
    >
      <ul
        v-if="cards"
        :class="{
          'flex overflow-hidden py-4': true,
          'lg:-mx-3': alignment === 'top',
        }"
      >
        <template
          v-for="(card, index) in cards"
          :key="index"
        >
          <li
            :class="{
              'carousel-card w-full shrink-0 px-3 md:w-1/2 lg:w-1/3': true,
            }"
          >
            <ReferenceCardCarousel
              v-if="card._type === 'referenceCard'"
              v-bind="card"
            />
            <ManualCard
              v-else-if="card._type === 'manualCard'"
              v-bind="card"
            />
          </li>
        </template>
      </ul>
      <CarouselCardControl
        :class="{
          'mt-1 flex items-center justify-center gap-4 lg:justify-start': true,
          'lg:hidden': alignment === 'left' || (cards?.length || 0) <= 3,
        }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CarouselCard } from '~/sanity/types';
import gsap from 'gsap';
import { useMediaQuery } from '@vueuse/core';
import ManualCard from '~/components/Atoms/ManualCard/ManualCard.vue';
import CarouselCardControl from './CarouselCardControl.vue';
import ReferenceCardCarousel from '../ReferenceCard/ReferenceCardCarousel.vue';
import Button from '~/components/Atoms/Button/Button.vue';

defineProps<CarouselCard>();

const carousel = ref<HTMLElement>();
const isLargeScreen = useMediaQuery('(min-width: 1024px)');
let interval: ReturnType<typeof setInterval>;
let loop: ReturnType<typeof seamlessLoop>;

onMounted(() => {
  if (!carousel.value) {
    console.warn('Carousel ref is undefined');
    return;
  }

  const boxes = gsap.utils.toArray('.carousel-card', carousel.value) as HTMLElement[];

  loop = seamlessLoop(boxes, { paused: true });

  carousel.value.querySelectorAll('.carousel-control-next').forEach((button) => {
    button.addEventListener('click', () => loop.next({ duration: 0.4, ease: 'power1.inOut' }));
  });

  carousel.value.querySelectorAll('.carousel-control-prev').forEach((button) => {
    button.addEventListener('click', () => loop.previous({ duration: 0.4, ease: 'power1.inOut' }));
  });

  if (!isLargeScreen.value) {
    interval = setInterval(() => {
      loop.next({ duration: 0.4, ease: 'power1.inOut' });
    }, 6000);
  }
});

onUnmounted(() => {
  if (interval) {
    clearInterval(interval);
  }
});

watch(isLargeScreen, (newValue) => {
  if (newValue) {
    if (interval) {
      clearInterval(interval);
    }
  } else {
    interval = setInterval(() => {
      loop.next({ duration: 0.4, ease: 'power1.inOut' });
    }, 2500);
  }
});
</script>
